import React from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
//#region types
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { TranslationKeys } from "translations";
import { FormHandles } from "models/propTypes/genericFormProps";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { CompanyHoldingRow } from "models/propTypes/externalCompanyHoldingsPropTypes";
//#endregion
//#region components
import TopTodoCard from "../../_partials/topTodoCard";
import { TitledContainerInner, EntityCardContent, Skeletons, AdaptiveDetailTableViewSmall } from "components/common";
import {
  ExternalCompanyHoldingsFooter,
  ExternalCompanyHoldingsFormContainer,
  useExternalCompanyHoldingsSnoozeAction,
} from "components/externalCompanyHoldings";
import { useShareRegisterService } from "serviceHooks";
import { Grid, Typography } from "@mui/material";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const ExternalCompanyHoldingsTodo: React.FC<TodoCardProps> = (props) => {
  const [isLoadingCompanyHoldings, setLoadingCompanyHoldings] = React.useState(false);
  const [companyHoldings, setCompanyHoldings] = React.useState<WintSuperkollAPIModel.CompanyHolding[]>([]);
  const [isFormValid, setIsFormValid] = React.useState(false);

  const declaration = props.item.externalCompanyHoldings;
  const loggedInUserName = useSelector((store: AppState) => store.auth.loggedInUser.name);
  const currentYear = new Date().getFullYear();

  const shareRegisterService = useShareRegisterService();
  const snoozeAction = useExternalCompanyHoldingsSnoozeAction(props.item.id, props.onActionSuccess);
  const formRef = React.useRef<FormHandles>(null);

  const onFormValidationChange = (isValid: boolean) => {
    setIsFormValid(isValid);
  };

  const getDetailsList = () => {
    const details: Array<CompanyHoldingRow> = companyHoldings
      .filter((d) => d.ownershipPercentage > 0)
      .map((d) => ({
        companyName: d.companyName ?? "",
        orgNr: d.orgNr ?? "",
        ownershipPercentage: d.ownershipPercentage,
      }));

    return details;
  };

  React.useEffect(() => {
    if (declaration) {
      setLoadingCompanyHoldings(true);
      const filter: WintSuperkollAPIModel.CompanyHoldingsFilter = {
        snapShotDate: DateHelper.getLastDayOfYear(currentYear - 1),
        page: 0,
        orderByDescending: false,
        thenByDescending: false,
        isAutoPaginating: false,
      };

      shareRegisterService
        .getCompanyHoldings(filter)
        .then((result) => {
          setCompanyHoldings(result.items ?? []);
        })
        .finally(() => setLoadingCompanyHoldings(false));
    }
  }, [declaration?.personId, declaration?.year]);

  return (
    <EntityCardContent
      topTodoCard={
        <TopTodoCard
          item={props.item}
          descriptionHeadingTwo={loggedInUserName}
          footerText={`${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Common.ReplyLatest
          )}: ${DateHelper.convertDateToShortISOString(new Date(currentYear, 0, 20))}`}
          quickActions={[snoozeAction]}
          noDescriptionTextWrap
        />
      }
      todoMainSection={
        <TitledContainerInner
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.CheckInformation)}
          subHeader={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.ExternalCompanyHoldings.SubTitle)}
        >
          <Grid item container rowGap={2} marginTop={1} className={cssExports.externalCompanyHoldingsTodoInfoViewer}>
            <Grid item>
              <Typography className={cssExports.description}>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.ExternalCompanyHoldings.K10Description)}
              </Typography>
            </Grid>
            <Grid item>
              <Typography className={cssExports.sectionTitle}>
                {`${LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.ExternalCompanyHoldings.ListOfKnownCompanies
                )} ${currentYear}`}
              </Typography>
              {isLoadingCompanyHoldings ? (
                <Skeletons.DetailTableViewSmallSkeleton />
              ) : (
                <AdaptiveDetailTableViewSmall<CompanyHoldingRow>
                  data={getDetailsList()}
                  properties={{
                    companyName: {
                      alignment: "left",
                      width: "45%",
                    },
                    orgNr: {
                      alignment: "left",
                      noWrap: true,
                    },
                    ownershipPercentage: {
                      alignment: "left",
                      className: cssExports.BlackText,
                      formatter: (row) => {
                        const value = row.ownershipPercentage;
                        const hasDecimals = value % 1 !== 0;
                        return hasDecimals ? `${value.toFixed(2)}%` : `${value}%`;
                      },
                    },
                  }}
                  hideHeader
                  addRowSeparators
                />
              )}
            </Grid>
            <Grid item>
              <ExternalCompanyHoldingsFormContainer
                ref={formRef}
                declaration={declaration}
                onFormValidationChange={onFormValidationChange}
                companyHoldings={companyHoldings}
              />
            </Grid>
          </Grid>
        </TitledContainerInner>
      }
      footer={
        <ExternalCompanyHoldingsFooter
          markAsDoneHandler={props.onActionSuccess}
          submitForm={() => (formRef.current?.submit ? formRef.current.submit() : Promise.resolve())}
          declarationId={props.item.guidId ?? props.item.id}
          isDisabled={!isFormValid}
        />
      }
    ></EntityCardContent>
  );
};

export default ExternalCompanyHoldingsTodo;
